import React, { useRef, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from '../hooks/useTranslation';
import { HeroContent } from '../components/landing/HeroContent';
import { Footer } from '../components/landing/Footer';
import { ChevronDown, ChevronUp } from 'lucide-react';
import { SkewedLinesBackground } from '../components/backgrounds/SkewedLinesBackground';
import { cn } from '../utils/classNames';
import { faqTranslations } from '../i18n/translations/pages';

// FAQ Item Component
const FAQItem: React.FC<{
  question: string;
  answer: string;
  isOpen: boolean;
  toggleOpen: () => void;
}> = ({ question, answer, isOpen, toggleOpen }) => {
  return (
    <div
      className="mb-4 w-full cursor-pointer rounded-xl bg-white p-4 shadow-sm border border-gray-200 hover:border-primary-500/20 hover:shadow-md transition-all duration-300 ease-out dark:bg-neutral-900 dark:border-neutral-800"
      onClick={toggleOpen}
    >
      <div className="flex items-start">
        <div className="relative mr-4 mt-1 h-6 w-6 flex-shrink-0">
          <ChevronUp
            className={cn(
              "absolute inset-0 h-6 w-6 transform text-black transition-all duration-200 dark:text-white",
              isOpen ? "rotate-0 scale-100" : "rotate-90 scale-0",
            )}
          />
          <ChevronDown
            className={cn(
              "absolute inset-0 h-6 w-6 transform text-black transition-all duration-200 dark:text-white",
              isOpen ? "rotate-90 scale-0" : "rotate-0 scale-100",
            )}
          />
        </div>
        <div className="flex-1">
          <h3 className="text-lg font-medium text-neutral-700 dark:text-neutral-200">
            {question}
          </h3>
          <AnimatePresence mode="wait">
            {isOpen && (
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: "auto", opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                transition={{ duration: 0.2, ease: "easeOut" }}
                className="overflow-hidden text-neutral-500 dark:text-neutral-400 mt-2"
              >
                {answer.split("").map((letter, index) => (
                  <motion.span
                    initial={{ opacity: 0, filter: "blur(5px)" }}
                    animate={{ opacity: 1, filter: "blur(0px)" }}
                    exit={{ opacity: 0, filter: "blur(0px)" }}
                    transition={{
                      duration: 0.2,
                      ease: "easeOut",
                      delay: index * 0.005,
                    }}
                    key={index}
                  >
                    {letter}
                  </motion.span>
                ))}
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>
    </div>
  );
};

// FAQ Category Component
const FAQCategory: React.FC<{
  title: string;
  questions: { question: string; answer: string }[];
}> = ({ title, questions }) => {
  const [openQuestion, setOpenQuestion] = useState<string | null>(null);

  const toggleQuestion = (question: string) => {
    setOpenQuestion(openQuestion === question ? null : question);
  };

  return (
    <div className="mb-12">
      <motion.h2 
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="text-2xl font-bold text-gray-900 dark:text-white mb-6"
      >
        {title}
      </motion.h2>
      <div className="space-y-4">
        {questions.map((faq, index) => (
          <FAQItem
            key={index}
            question={faq.question}
            answer={faq.answer}
            isOpen={openQuestion === faq.question}
            toggleOpen={() => toggleQuestion(faq.question)}
          />
        ))}
      </div>
    </div>
  );
};

const FaqPage: React.FC = () => {
  const { locale } = useTranslation();
  const containerRef = useRef<HTMLDivElement>(null);
  const parentRef = useRef<HTMLDivElement>(null);
  
  // Get translations for current language, fallback to English if not available
  const translations = faqTranslations[locale as keyof typeof faqTranslations] || faqTranslations.en;
  
  const categories = [
    {
      title: translations.categories.general.title,
      questions: translations.categories.general.questions
    },
    {
      title: translations.categories.features.title,
      questions: translations.categories.features.questions
    },
    {
      title: translations.categories.technical.title,
      questions: translations.categories.technical.questions
    }
  ];

  return (
    <div className="min-h-screen bg-neutral-50 dark:bg-neutral-900" ref={parentRef}>
      {/* Hero Section with Navigation */}
      <div className="relative h-[50vh] min-h-[400px] w-full overflow-hidden bg-white dark:bg-neutral-950" ref={containerRef}>
        {/* Skewed Lines Background */}
        <SkewedLinesBackground />
        
        {/* Navbar */}
        <HeroContent containerRef={containerRef} parentRef={parentRef} showHeroProps={false} />
        
        {/* FAQ Title */}
        <div className="absolute inset-0 z-10 flex items-center justify-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
            className="text-center"
          >
            <motion.h1 
              className="text-4xl md:text-6xl font-bold text-center text-gray-900 dark:text-white"
            >
              {translations.title}
            </motion.h1>
            <motion.p
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.3 }}
              className="mt-4 max-w-2xl mx-auto text-center text-base text-neutral-800 dark:text-neutral-200"
            >
              {translations.subtitle}
            </motion.p>
          </motion.div>
        </div>
      </div>

      {/* FAQ Content */}
      <div className="max-w-4xl mx-auto px-4 py-16 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <p className="text-lg text-gray-600 dark:text-gray-300">
            {translations.cantFind}{" "}
            <a
              href="/contact"
              className="font-medium text-primary-600 hover:text-primary-500 transition-colors duration-200"
            >
              {translations.contactUs}
            </a>
          </p>
        </div>

        {/* FAQ Categories */}
        {categories.map((category, index) => (
          <FAQCategory
            key={index}
            title={category.title}
            questions={category.questions}
          />
        ))}
      </div>

      {/* Footer */}
      <Footer />
    </div>
  );
};

export default FaqPage; 